import { compactFlatten, isString, merge, pluralize } from '@art-suite/art-core-ts';
import { zodLessParse } from '@kaiizen/zod-less';
import { getUniqueId } from '../commonLib';
import { pureFunctionalMap, pureFunctionalMerge, pureFunctionalRecordMap } from '../pureFunctionalLib';
import { ScanPartContext } from '../ScanRoot';
import { mapScanTemplatePartsFromRoot, ScanTemplateRoot, ScanTemplateSkuOption, ScanTemplateValue, ScanTemplateValues } from '../ScanTemplateRoot';
import { parseScanTemplateRoot, ScanTemplateValidationDetails, ScanTemplateValidationError, validateScanTemplateData } from '../validateTemplate';
import { repairStandardizedTemplateOtherTasks, validateStandardizedTemplateOtherTasks } from './repairStandardizedTemplate';
import { getStandardTotalCostValue } from './standardLineItemGenerator';
import {
  isGroupRoomSubtype,
  STANDARD_ROOM_GROUP_VALUES,
  StandardScanTemplateGroup,
  StandardScanTemplateLineItem,
  StandardScanTemplateOption,
  StandardScanTemplateRootSchema,
  StandardScanTemplateTask,
} from './StandardScanTemplateSchemas';

const STANDARDIZED_TEMPLATE_BRAND_NAME = 'Kaiizen Standardized Template for Renovations';
const MISSING_LINE_ITEM_COST_MESSAGE = 'All Line Items must either have a materialCost or a laborCost value';
const TOTAL_COST_NOT_USD_MESSAGE = 'A Line Item totalCost must be usd';

export type StandardizationOptions = {
  flattenLineItemsWithMultipleSkus?: boolean;
  strict?: boolean; // if true, will not auto-fix small errors; will throw errors for all inconsistencies (this is a WIP)
};

const addErrorOrThrow = (issues: ScanTemplateValidationDetails[] | undefined, message: string, scanPartContext: ScanPartContext) => {
  if (issues) {
    issues.push({ message, ...scanPartContext });
    return {};
  }
  throw new Error(message);
};

/**
 * A valid standard line item needs exactly two things:
 * - a materialCost and/or a laborCost value
 * - a usd totalCost value (derived from the cost values when absent or valueless)
 * A line item with a product (a SKU, or one picked at scope time) never has a
 * template price value: its price always comes from its product.
 * All other values (quantity, laborPrice, and any custom names) and
 * every equation the author wrote are left untouched. Equation correctness is
 * enforced by the general template validation (validateScanTemplateData).
 */
const getStandardLineItemValues = (
  { hasProduct, unevaluatedValues }: { hasProduct: boolean; unevaluatedValues?: ScanTemplateValues },
  issues: ScanTemplateValidationDetails[],
  lineItemContext: ScanPartContext,
): ScanTemplateValues => {
  if (!unevaluatedValues) {
    if (hasProduct) {
      unevaluatedValues = {
        price: { type: 'usd' },
        quantity: { type: 'count', value: 0 },
        materialCost: { type: 'usd', value: 'price * quantity' },
      };
    } else {
      addErrorOrThrow(issues, MISSING_LINE_ITEM_COST_MESSAGE, lineItemContext);
      return {};
    }
  }

  let { price, materialCost, laborCost, laborPrice, totalCost, ...otherValues } = unevaluatedValues as Record<string, ScanTemplateValue | undefined>;

  // if price was used instead of laborPrice, but we only have laborCost, swap them
  // (rewriting any "price" references in the laborCost equation to match)
  if (price && laborCost && !materialCost && !laborPrice) {
    laborPrice = price;
    price = undefined;
    if (isString(laborCost.value)) laborCost = { ...laborCost, value: laborCost.value.replace(/\bprice\b/g, 'laborPrice') };
  }

  if (hasProduct && price) {
    const { value: _templatePrice, ...priceWithoutValue } = price;
    price = priceWithoutValue;
  }

  if (!laborCost && !materialCost) addErrorOrThrow(issues, MISSING_LINE_ITEM_COST_MESSAGE, lineItemContext);

  if (totalCost && totalCost.type !== 'usd') addErrorOrThrow(issues, TOTAL_COST_NOT_USD_MESSAGE, lineItemContext);

  if (totalCost?.value == null) totalCost = { ...totalCost, ...getStandardTotalCostValue(!!materialCost, !!laborCost) };

  return merge({ ...otherValues, price, materialCost, laborCost, laborPrice, totalCost });
};

export const validateStandardizedTemplateLineItem = (
  lineItem: StandardScanTemplateLineItem,
  issues: ScanTemplateValidationDetails[],
  options: StandardizationOptions,
  optionContext: ScanPartContext,
): StandardScanTemplateLineItem | StandardScanTemplateLineItem[] => {
  const lineItemContext = { ...optionContext, lineItemId: lineItem.id };
  const flatteningLineItemsWithMultipleSkus = options?.flattenLineItemsWithMultipleSkus && lineItem.skuIdOptions && lineItem.skuIdOptions.length > 1;
  const result = {
    ...lineItem,
    values: getStandardLineItemValues(
      {
        hasProduct: !!lineItem.skuIdOptions?.length || !!lineItem.useScopeStageSkus,
        unevaluatedValues: lineItem.values,
      },
      issues,
      lineItemContext,
    ),
  };
  if (flatteningLineItemsWithMultipleSkus) {
    return lineItem.skuIdOptions!.map((skuIdOption: string | ScanTemplateSkuOption, index: number) => ({
      ...result,
      id: getUniqueId(),
      name: `${lineItem.name} - ${index + 1}`,
      skuIdOptions: [skuIdOption],
      values: {
        ...result.values,
        ...(result.values?.quantity ? { quantity: { ...result.values.quantity, value: 0 } } : {}),
      },
    }));
  }
  return result;
};

const lineItemNeedsDefaultQuantity = (lineItem: StandardScanTemplateLineItem) => {
  return lineItem.values?.quantity && lineItem.values?.quantity?.value === undefined;
};

const validateStandardizedTemplateLineItemDefaultQuantity = (lineItemTemplates: StandardScanTemplateLineItem[]) => {
  // Default quantity is always 0. It cannot be 1, because the UI interprets
  // that as being automatically selected and added to the Scope -- whereas in
  // practice, the user might not want to select that Option, or even that
  // Task at all.
  const defaultQuantity = 0;
  const needsUpdating = lineItemTemplates.some(lineItem => lineItemNeedsDefaultQuantity(lineItem));
  if (!needsUpdating) return lineItemTemplates;
  return lineItemTemplates.map(lineItem =>
    pureFunctionalMerge(
      lineItem,
      lineItemNeedsDefaultQuantity(lineItem)
        ? {
          ...lineItem,
          values: {
            ...lineItem.values,
            quantity: { ...lineItem.values?.quantity, value: defaultQuantity },
          },
        }
        : lineItem,
    )
  );
};

export const validateStandardizedTemplateOption = (
  option: StandardScanTemplateOption,
  issues: ScanTemplateValidationDetails[],
  options: StandardizationOptions,
  taskContext: ScanPartContext,
): StandardScanTemplateOption => {
  const optionContext = { ...taskContext, optionId: option.id };
  let result = pureFunctionalMerge(option, {
    lineItemTemplates: compactFlatten(pureFunctionalMap<StandardScanTemplateLineItem | StandardScanTemplateLineItem[]>(
      option.lineItemTemplates ?? [],
      lineItem => validateStandardizedTemplateLineItem(lineItem as StandardScanTemplateLineItem, issues, options, optionContext),
    )),
  });

  if (result.lineItemTemplates) {
    result = pureFunctionalMerge(result, {
      lineItemTemplates: validateStandardizedTemplateLineItemDefaultQuantity(result.lineItemTemplates),
    });
  }
  return result;
};

export const validateStandardizedTemplateTask = (
  task: StandardScanTemplateTask,
  issues: ScanTemplateValidationDetails[],
  options: StandardizationOptions,
): StandardScanTemplateTask => {
  const taskContext = { taskId: task.id };
  const result = pureFunctionalMerge(task, {
    optionTemplates: pureFunctionalMap(
      task.optionTemplates,
      option => validateStandardizedTemplateOption(option, issues, options, taskContext),
    ),
  });
  return result;
};

const getValuesWithMeaningType = (values: ScanTemplateValues | undefined): ScanTemplateValues => {
  const result: ScanTemplateValues = {};
  for (const [name, value] of Object.entries(values ?? {})) {
    if (value.meaningType) result[name] = value;
  }
  return result;
};

/**
 * Normalizes group values for the standardized template.
 * - For room subtypes, the values must be STANDARD_ROOM_GROUP_VALUES, plus any number of
 *   meaning-typed values (e.g. flooringAreaMeasurement); the standard values win on name conflicts.
 * - For non-room subtypes, preserves existing values or defaults to {}.
 */
export const validateStandardizedTemplateGroup = (
  group: StandardScanTemplateGroup,
  issues?: ScanTemplateValidationDetails[],
  options?: StandardizationOptions,
): StandardScanTemplateGroup =>
  pureFunctionalMerge(group, {
    values: isGroupRoomSubtype(group.subtype)
      ? { ...getValuesWithMeaningType(group.values), ...STANDARD_ROOM_GROUP_VALUES }
      : (group.values ?? {}),
  });

/**
 * Values with meaningType 'flooringAreaMeasurement' must:
 * - appear only on Group scan parts
 * - have a name ending in "Area"
 * - have type "sqft"
 * - have no default value/equation
 */
const validateFlooringAreaValues = (scanTemplateRoot: ScanTemplateRoot, issues: ScanTemplateValidationDetails[]): void => {
  mapScanTemplatePartsFromRoot(scanTemplateRoot, (node, nodeType) => {
    for (const [name, value] of Object.entries(node.values ?? {})) {
      if (value?.meaningType !== 'flooringAreaMeasurement') continue;
      if (nodeType !== 'groupTemplate') {
        const context: ScanPartContext = nodeType === 'taskTemplate'
          ? { taskId: node.id }
          : nodeType === 'optionTemplate'
          ? { optionId: node.id }
          : { lineItemId: node.id };
        issues.push({ message: `Flooring area value "${name}" is only allowed on Groups (found on a ${nodeType})`, ...context });
        continue;
      }
      const context: ScanPartContext = { groupId: node.id };
      if (!name.endsWith('Area')) issues.push({ message: `Flooring area value "${name}" must have a name ending in "Area"`, ...context });
      if (value.type !== 'sqft') issues.push({ message: `Flooring area value "${name}" must have type "sqft"`, ...context });
      if (value.value !== undefined) issues.push({ message: `Flooring area value "${name}" must not have a default value or equation`, ...context });
    }
    return node;
  });
};

const validateStandardizedTemplateGroupSubtypes = (scanTemplateRoot: ScanTemplateRoot, issues: ScanTemplateValidationDetails[] = []): void => {
  const subtypeCounts: Record<string, number> = {};
  scanTemplateRoot.groupTemplates?.forEach((group: any) => {
    const subtype = (group as any).subtype as string | undefined;
    if (subtype) subtypeCounts[subtype] = (subtypeCounts[subtype] ?? 0) + 1;
  });
  if (!subtypeCounts.otherBedroom) issues.push({ message: 'Standard Template requires at least one other bedroom group' });
  if (!subtypeCounts.otherBathroom) issues.push({ message: 'Standard Template requires at least one other bathroom group' });
  Object.keys(subtypeCounts).forEach(subtype => {
    if ((subtypeCounts[subtype] ?? 0) > 1) issues.push({ message: `Standard Template allows at most one group template for subtype '${subtype}'` });
  });
};

/**
 * Some parts of the "Kaiizen Standardized Template for Renovations" can be automatically applied
 * In the interest of minimizing errors thrown, this only throws validation errors if there's no way to correct them.
 * @param scanTemplateRoot - The template to standardize (and normalized)
 * @returns The standardized template.
 */
export const validateStandardTemplate = (scanTemplateRoot: any, options: StandardizationOptions = {}): ScanTemplateRoot => {
  // parse and normalize the template - throws error with all structural errors
  const normalizedValidScanTemplateRoot = parseScanTemplateRoot(scanTemplateRoot);
  const standardScanTemplateRoot = zodLessParse(StandardScanTemplateRootSchema, normalizedValidScanTemplateRoot);

  // fill in and check for additional standard template rules; errors are accumulated in the errors array
  const issues: ScanTemplateValidationDetails[] = [];
  let result: ScanTemplateRoot = pureFunctionalMerge(standardScanTemplateRoot, {
    groupTemplates: pureFunctionalMap(standardScanTemplateRoot.groupTemplates, group => validateStandardizedTemplateGroup(group, issues, options)),
    taskTemplatesById: pureFunctionalRecordMap(standardScanTemplateRoot.taskTemplatesById, task => validateStandardizedTemplateTask(task, issues, options)),
  });
  if (options.strict) {
    validateStandardizedTemplateOtherTasks(result, issues);
  } else {
    result = repairStandardizedTemplateOtherTasks(result, issues);
  }

  validateStandardizedTemplateGroupSubtypes(result, issues);
  validateFlooringAreaValues(result, issues);
  if (issues.length > 0) {
    throw new ScanTemplateValidationError(
      `${STANDARDIZED_TEMPLATE_BRAND_NAME} Validation - ${pluralize('error', issues.length)}:\n` + issues.map(i => i.message).join('\n'),
      issues,
    );
  }

  // The function validateScanTemplateData handles its own issues list internally,
  // and throws a ScanTemplateValidationError if there are any issues.
  // That's why we don't pass the issues array here.
  // To retain backwards compatibility, we'll default to strict mode
  // unless options.strict is explicitly set to false.
  const strictMode = options.strict !== false;
  validateScanTemplateData(result, !strictMode);

  return result;
};

/**
 * @deprecated Use validateStandardizedTemplate instead
 * @param templateRoot
 * @returns
 */
export const standardizeAndValidateTemplate = (templateRoot: any, options?: StandardizationOptions): ScanTemplateRoot =>
  validateStandardTemplate(templateRoot, options);
